import { useId, type ReactNode } from 'react';
import { ArrowBigUp } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { CommunityFeedItem } from '@shared/types';

import { PaletteAvatar } from '@/components/PaletteAvatar';
import { Poster } from '@/components/Poster';
import { ErrorState } from '@/components/StateBlock';
import { TextLink } from '@/components/TextLink';
import { Skeleton } from '@/components/ui/skeleton';
import { useMyCalendar } from '@/features/calendar/api';
import { airDay, startOfDay } from '@/features/calendar/dates';
import { useWatchingFriends, type WatchingFriend } from '@/features/friends/api';
import { localized } from '@/i18n/localized';
import { useLocale } from '@/i18n/locale';
import { cn } from '@/lib/utils';

import { commentTarget, threadLink } from './links';

/*
 * The things that change by the hour rather than by the post: Airing this
 * week, Friends watching now, Hot on your shelf. Plain sections on the canvas
 * — a `text-headline` heading and the content, no box. At `lg` they stack in
 * the sticky right column; below it they sit above the timeline and their
 * lists become horizontal strips (CSS only, one render). Each section owns
 * its loading / empty / error state — one failing never blanks the others.
 */

const WATCHING_LIMIT = 8;
const AIRING_LIMIT = 5;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** A horizontal strip below `lg`, the column's own layout at `lg`. */
const STRIP = 'scrollbar-none m-0 flex list-none snap-x gap-3 overflow-x-auto p-0';

function SideSection({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <section aria-labelledby={id} className={cn('flex min-w-0 flex-col gap-3', className)}>
      {/* min-h-9 matches the timeline's heading row, so the column's first
          heading top-aligns with "From your friends" at lg. */}
      <div className="flex min-h-9 items-center justify-between gap-3">
        <h2 id={id} className="text-headline text-ink">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function WatchingTile({ entry }: { entry: WatchingFriend }) {
  const { locale, t } = useLocale();
  const name = entry.friend.displayName?.trim() || entry.friend.username;
  const title = localized(entry.title.title, entry.title.titleBr, locale);
  return (
    <li className="w-16 shrink-0 snap-start lg:w-auto">
      <Link
        to={`/user/${entry.friend.username}`}
        aria-label={t('tonight.watchingA11y', { name, title })}
        className="flex flex-col gap-2 rounded-sm"
      >
        <span className="relative block">
          <Poster
            id={entry.title.id}
            name={title}
            palette={entry.title.palette}
            imageUrl={entry.title.posterUrl}
            imageFallbackUrl={entry.title.posterFallbackUrl}
            sizes="72px"
            layout={false}
            fallbackTitle={false}
          />
          <span className="absolute -bottom-1.5 -right-1.5 rounded-full bg-bg p-0.5">
            <PaletteAvatar
              name={name}
              avatarUrl={entry.friend.avatarUrl}
              palette={entry.friend.avatarPalette}
              className="size-6 text-micro"
              alt=""
            />
          </span>
        </span>
        <span className="w-full truncate text-caption text-ink-secondary">{name}</span>
      </Link>
    </li>
  );
}

/**
 * Who is watching what right now, as small covers with the friend's avatar.
 * Renders nothing when no friend is watching anything (the timeline's own
 * empty state covers "no follows").
 */
export function WatchingNow() {
  const { t } = useLocale();
  const query = useWatchingFriends(WATCHING_LIMIT);

  let body: ReactNode;
  if (query.isPending) {
    body = (
      <div role="status" aria-label={t('tonight.loadingWatching')} className="flex gap-3">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="aspect-[2/3] w-16 rounded-sm" />
        ))}
      </div>
    );
  } else if (query.isError) {
    body = (
      <ErrorState size="inline" title={t('tonight.error.watching')} onRetry={() => void query.refetch()} />
    );
  } else if ((query.data ?? []).length === 0) {
    return null;
  } else {
    body = (
      <ul className={cn(STRIP, 'pb-2 lg:grid lg:grid-cols-4 lg:gap-x-3 lg:gap-y-4 lg:overflow-visible')}>
        {(query.data ?? []).map((entry) => (
          <WatchingTile key={`${entry.friend.publicUserId}-${entry.title.id}`} entry={entry} />
        ))}
      </ul>
    );
  }

  return <SideSection title={t('tonight.watchingNow')}>{body}</SideSection>;
}

/** "Fri" / "sex" — the local air day (air dates are date-only, read as local days). */
function dayLabel(iso: string, locale: string): string {
  return airDay(iso)
    .toLocaleDateString(locale === 'pt-BR' ? 'pt-BR' : 'en-US', { weekday: 'short' })
    .replace(/\.$/, '');
}

/** The next seven days of tracked episodes (≤ 5 rows) → `/calendar`. */
export function AiringThisWeek() {
  const { locale, t } = useLocale();
  const query = useMyCalendar();
  const today = startOfDay(new Date()).getTime();

  let body: ReactNode;
  if (query.isPending) {
    body = (
      <div role="status" aria-label={t('tonight.loadingAiring')} className="flex flex-col gap-3">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-3 w-8" />
            <Skeleton className="aspect-[2/3] w-7" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ))}
      </div>
    );
  } else if (query.isError) {
    body = (
      <ErrorState size="inline" title={t('tonight.error.airing')} onRetry={() => void query.refetch()} />
    );
  } else {
    const rows = (query.data?.entries ?? [])
      .filter((entry) => {
        const at = airDay(entry.airDate).getTime();
        return at >= today && at < today + WEEK_MS;
      })
      .sort((a, b) => airDay(a.airDate).getTime() - airDay(b.airDate).getTime())
      .slice(0, AIRING_LIMIT);
    body =
      rows.length === 0 ? (
        <p className="text-caption text-ink-secondary">{t('tonight.nothingAiring')}</p>
      ) : (
        <ul className={cn(STRIP, '-mx-2 gap-1 lg:flex-col lg:gap-0 lg:overflow-visible')}>
          {rows.map((entry) => {
            const name = localized(entry.title, entry.titleBr, locale);
            return (
              <li
                key={`${entry.titleId}-${entry.seasonNumber}-${entry.episodeNumber}`}
                className="w-60 shrink-0 snap-start lg:w-auto"
              >
                <Link
                  to={`/show/${entry.titleId}`}
                  className="grid grid-cols-[2.5rem_28px_minmax(0,1fr)] items-center gap-3 rounded px-2 py-2 transition-colors duration-fast hover:bg-surface-alt"
                >
                  <span className="text-caption text-timecode text-ink-secondary">
                    {dayLabel(entry.airDate, locale)}
                  </span>
                  <span className="block w-7">
                    <Poster
                      id={entry.titleId}
                      name={name}
                      palette={entry.palette}
                      imageUrl={entry.posterUrl}
                      imageFallbackUrl={entry.posterFallbackUrl}
                      sizes="28px"
                      layout={false}
                      fallbackTitle={false}
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-subhead font-medium text-ink">{name}</span>
                    <span className="block text-caption text-timecode text-ink-tertiary">
                      {t('episode.code', { season: entry.seasonNumber, episode: entry.episodeNumber })}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      );
  }

  return (
    <SideSection
      title={t('tonight.airingThisWeek')}
      action={
        <TextLink to="/calendar" variant="quiet" className="text-caption">
          {t('tonight.seeCalendar')}
        </TextLink>
      }
    >
      {body}
    </SideSection>
  );
}

/** The top trending comment on the user's shelf, when the timeline isn't showing it. */
export function HotOnShelf({ item }: { item: CommunityFeedItem }) {
  const { locale, t } = useLocale();
  const titleName = localized(item.title.title, item.title.titleBr, locale);
  const link = threadLink(commentTarget(item.comment), item.title.id);
  return (
    <SideSection title={t('tonight.hotOnShelf')}>
      <Link to={link} className="group block rounded">
        <p className="line-clamp-4 border-l-2 border-border pl-3 text-body text-ink group-hover:underline group-hover:decoration-border group-hover:underline-offset-4">
          {item.comment.body}
        </p>
        <p className="mt-2 flex items-center gap-3 text-caption text-ink-tertiary">
          <span className="truncate">{titleName}</span>
          <span className="inline-flex items-center gap-1">
            <ArrowBigUp className="size-3.5 shrink-0" strokeWidth={1.75} aria-hidden />
            <span className="tabular-nums">
              <span className="sr-only">{t('tonight.upvotes')} </span>
              {item.comment.score}
            </span>
          </span>
        </p>
      </Link>
    </SideSection>
  );
}
